import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { api } from "../lib/config";
import { Check } from "lucide-react";

const QUERY_TYPES = ["Booking Question", "Payment Issue", "Complaint", "General Question"];

export default function QueryFormModal({ open, onOpenChange }) {
  const [form, setForm] = useState({ query_type: "General Question", name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries/contact", form);
      setDone(true);
      toast.success("Query sent — we'll be in touch shortly.");
      setTimeout(() => {
        onOpenChange(false);
        setDone(false);
        setForm({ query_type: "General Question", name: "", email: "", phone: "", message: "" });
      }, 1600);
    } catch (err) {
      toast.error("Something went wrong — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-owt-surface border-owt-border" data-testid="query-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-owt-forest">Send us a query</DialogTitle>
          <DialogDescription className="text-owt-muted">
            We reply to every message within one working day.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-10 text-center">
            <div className="mx-auto w-14 h-14 rounded-full border-2 border-owt-terracotta flex items-center justify-center">
              <Check className="w-7 h-7 text-owt-terracotta"/>
            </div>
            <p className="mt-4 font-display text-2xl text-owt-forest">Thanks — we'll be in touch!</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Query Type</label>
              <Select value={form.query_type} onValueChange={(v) => setForm({ ...form, query_type: v })}>
                <SelectTrigger data-testid="query-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{QUERY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Name</label>
                <Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="query-name" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Email</label>
                <Input required type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} data-testid="query-email" />
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Phone (optional)</label>
              <Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} data-testid="query-phone" />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted block mb-1">Message</label>
              <Textarea required rows={4} value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})} data-testid="query-message" />
            </div>
            <button
              disabled={loading}
              type="submit"
              data-testid="query-submit"
              className="w-full bg-owt-forest text-owt-surface py-3 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-owt-terracotta transition-colors disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send Query"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
